import React, { useImperativeHandle, useRef, useState } from "react";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import S from "./ContentBox.module.scss";

export interface DA_ContentBoxRef {
    alert: (ms?: number) => Promise<boolean>;
}

export interface DA_ContentBoxProps {
    children: React.ReactNode;
    smallVerticalPadding?: boolean;
    mediumVerticalPadding?: boolean;
    smallMargin?: boolean;
    mediumMargin?: boolean;
    noPadding?: boolean;
    noMargin?: boolean;
    loading?: boolean;
    className?: string;
    onClick?: (event: React.MouseEvent) => void;
    noShadow?: boolean;
    noBorderRadius?: boolean;
    noBackgroundColor?: boolean;
}

export const DA_ContentBox = React.forwardRef(
    (
        {
            children,
            smallVerticalPadding,
            mediumVerticalPadding,
            smallMargin,
            mediumMargin,
            noPadding,
            noMargin,
            noShadow,
            noBorderRadius,
            noBackgroundColor,
            loading,
            className,
            onClick,
        }: DA_ContentBoxProps,
        ref
    ): JSX.Element => {
        const [showAlert, setShowAlert] = useState(false);
        const alertTimeout = useRef<NodeJS.Timeout | null>(null);

        useImperativeHandle(
            ref,
            (ms = 1500) => ({
                alert: () => {
                    if (alertTimeout.current) {
                        clearTimeout(alertTimeout.current);
                    }

                    setShowAlert(true);

                    return new Promise((resolve) => {
                        alertTimeout.current = setTimeout(() => {
                            setShowAlert(false);
                            resolve(true);
                        }, ms);
                    });
                },
            }),
            []
        );

        return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
            <section
                className={cn(
                    S.container,
                    smallVerticalPadding && S.smallVerticalPadding,
                    mediumVerticalPadding && S.mediumVerticalPadding,
                    smallMargin && S.smallMargin,
                    mediumMargin && S.mediumMargin,
                    noPadding && S.noPadding,
                    noMargin && S.noMargin,
                    noShadow && S.noShadow,
                    noBorderRadius && S.noBorderRadius,
                    noBackgroundColor && S.noBackgroundColor,
                    className
                )}
                onClick={onClick}
            >
                {children}

                <div
                    className={cn(
                        S.indicator,
                        S.loadingIndicator,
                        loading && !showAlert && S.enabled
                    )}
                />
                <div className={cn(S.indicator, S.alertIndicator, showAlert && S.enabled)} />
            </section>
        );
    }
);

DA_ContentBox.displayName = "DA_ContentBox";
